import { useEffect, useMemo, useRef, useState } from 'react'

function ProgressBar({
  stages = ['IDEAS', 'RESEARCH', 'PLANNING', 'SUBMITTING', 'IMPLEMENTATION', 'COMPLETE'],
  autoCycle = false,
  cycleIntervalMs = 2500,
  currentIndex: controlledIndex,
  onChangeIndex,
  details = [],
  images = [],
  interactive = false,
  autoSurfCompleted = false,
  maskCurrent = false,
  showPercent = false,
  showArrowIndicator = false,
  showHeader = false,
}) {
  const totalPoints = useMemo(() => Math.max(stages.length, 2), [stages.length])
  const [uncontrolledIndex, setUncontrolledIndex] = useState(0)
  const currentIndex = controlledIndex ?? uncontrolledIndex
  const [hoverIndex, setHoverIndex] = useState(null)
  const trackRef = useRef(null)
  const trackLineRef = useRef(null)
  const isDraggingRef = useRef(false)
  const [displayIndex, setDisplayIndex] = useState(0)
  const [modalIndex, setModalIndex] = useState(null)
  const barTopPx = 48 // vertical position of the bar (in px) to reduce top whitespace
  const [barCenter, setBarCenter] = useState(barTopPx + 6)

  useEffect(() => {
    const computeCenter = () => {
      const h = trackLineRef.current?.offsetHeight || 8
      setBarCenter(barTopPx + h / 2)
    }
    computeCenter()
    window.addEventListener('resize', computeCenter)
    return () => window.removeEventListener('resize', computeCenter)
  }, [])

  useEffect(() => {
    if (!autoCycle) return
    const intervalId = setInterval(() => {
      const nextIndex = controlledIndex === undefined ? (uncontrolledIndex + 1) % totalPoints : (controlledIndex + 1) % totalPoints
      if (controlledIndex === undefined) {
        setUncontrolledIndex(nextIndex)
      } else if (onChangeIndex) {
        onChangeIndex(nextIndex)
      }
    }, cycleIntervalMs)
    return () => clearInterval(intervalId)
  }, [autoCycle, cycleIntervalMs, totalPoints, controlledIndex, uncontrolledIndex, onChangeIndex])

  const masked = maskCurrent && !interactive
  const uiIndex = masked ? 0 : currentIndex
  const progressPercent = (uiIndex / (totalPoints - 1)) * 100
  const progressPercentFill = (currentIndex / (totalPoints - 1)) * 100

  const commitIndex = (newIndex) => {
    const bounded = Math.min(totalPoints - 1, Math.max(0, newIndex))
    if (controlledIndex === undefined) {
      setUncontrolledIndex(bounded)
    }
    if (onChangeIndex) onChangeIndex(bounded)
  }

  const getIndexFromClientX = (clientX) => {
    const rect = trackRef.current?.getBoundingClientRect()
    if (!rect) return currentIndex
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width))
    return Math.round(ratio * (totalPoints - 1))
  }

  // Set display index to the furthest completed project by default
  useEffect(() => {
    if (currentIndex > 0) {
      setDisplayIndex(currentIndex - 1) // Show the last completed stage
    } else {
      setDisplayIndex(0)
    }
  }, [currentIndex])

  const truncate = (text, maxLen = 120) => {
    if (!text) return ''
    return text.length > maxLen ? text.slice(0, maxLen - 1) + '…' : text
  }

  useEffect(() => {
    if (!interactive) return
    const handleMove = (e) => {
      if (!isDraggingRef.current) return
      const clientX = e.touches ? e.touches[0].clientX : e.clientX
      commitIndex(getIndexFromClientX(clientX))
    }
    const stopDrag = () => { isDraggingRef.current = false }
    window.addEventListener('mousemove', handleMove)
    window.addEventListener('touchmove', handleMove)
    window.addEventListener('mouseup', stopDrag)
    window.addEventListener('touchend', stopDrag)
    return () => {
      window.removeEventListener('mousemove', handleMove)
      window.removeEventListener('touchmove', handleMove)
      window.removeEventListener('mouseup', stopDrag)
      window.removeEventListener('touchend', stopDrag)
    }
  }, [interactive, totalPoints, currentIndex])

  return (
    <div className="w-full">
      {showHeader && (
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm md:text-base font-semibold text-gray-800">TBVA Project Progress</h3>
          {showPercent && !masked && (
            <span className="text-xs md:text-sm text-gray-600">{Math.round(progressPercent)}%</span>
          )}
        </div>
      )}

      <div
        className="relative px-1 md:px-2 pt-4 pb-32 md:pb-56 overflow-visible"
        ref={trackRef}
        onMouseDown={(e) => {
          if (!interactive) return
          isDraggingRef.current = true
          commitIndex(getIndexFromClientX(e.clientX))
        }}
        onTouchStart={(e) => {
          if (!interactive) return
          isDraggingRef.current = true
          commitIndex(getIndexFromClientX(e.touches[0].clientX))
        }}
      >
        {/* Track */}
        <div ref={trackLineRef} className="absolute left-0 right-0 h-2 md:h-3 bg-white/20 rounded-full z-0" style={{ top: barTopPx }} />

        {/* Progress fill (always show for users so the pipe is colored) */}
        <div
          className="absolute left-0 h-2 md:h-3 bg-secondary rounded-full transition-all duration-700 ease-out z-0"
          style={{ width: `${progressPercentFill}%`, top: barTopPx }}
        />

        {/* Display preview for completed sections */}
        {currentIndex > 0 && (
          <div
            className="absolute z-30"
            style={{
              left: `${(((hoverIndex !== null ? hoverIndex : displayIndex) + 0.5) / (totalPoints - 1)) * 100}%`,
              top: barCenter + 36,
              transform: 'translate(-50%, 0)',
              transition: hoverIndex !== null ? 'left 200ms ease-out' : 'left 650ms ease-in-out'
            }}
          >
            <div
              className="bg-black/90 backdrop-blur-sm border border-secondary/20 shadow-lg rounded-lg overflow-hidden w-36 md:w-56 cursor-pointer pointer-events-auto"
              onClick={() => setModalIndex(hoverIndex !== null ? hoverIndex : displayIndex)}
            >
              {images && images[hoverIndex !== null ? hoverIndex : displayIndex] ? (
                <div className="w-full h-16 md:h-28 bg-black/20 overflow-hidden">
                  <img
                    src={images[hoverIndex !== null ? hoverIndex : displayIndex]}
                    alt={stages[hoverIndex !== null ? hoverIndex : displayIndex]}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.currentTarget.style.display = 'none' }}
                  />
                </div>
              ) : null}
              <div className="p-2 md:p-3">
                <div className="text-[5px] md:text-xs font-semibold text-white mb-1">{stages[hoverIndex !== null ? hoverIndex : displayIndex]}</div>
                {details && details[hoverIndex !== null ? hoverIndex : displayIndex] ? (
                  <div className="text-[5px] md:text-xs text-white/70 leading-snug">
                    {truncate(details[hoverIndex !== null ? hoverIndex : displayIndex])}
                  </div>
                ) : (
                  <div className="text-[8px] md:text-xs text-white/50">No details</div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Arrow that points to current progress point */}
        {showArrowIndicator && (
          <div
            className="absolute z-40 pointer-events-none"
            style={{
              left: `${(currentIndex / (totalPoints - 1)) * 100}%`,
              top: barCenter + 8,
              transform: 'translate(-50%, 0)'
            }}
            aria-hidden
          >
            <div style={{
              width: 0,
              height: 0,
              borderLeft: '8px solid transparent',
              borderRight: '8px solid transparent',
              borderTop: '10px solid #FFBF00'
            }} />
          </div>
        )}

        {/* Draggable knob */}
        {!masked && (
          <div
            className={`absolute -translate-y-1/2 -translate-x-1/2 w-5 h-5 md:w-6 md:h-6 rounded-full border-2 ${interactive ? 'cursor-grab active:cursor-grabbing' : 'cursor-default'} ${interactive ? 'border-secondary bg-white shadow' : 'border-white/30 bg-white'} z-10`}
            style={{ left: `${progressPercent}%`, top: barCenter }}
            onMouseDown={(e) => { if (!interactive) return; e.stopPropagation(); isDraggingRef.current = true }}
            onTouchStart={(e) => { if (!interactive) return; e.stopPropagation(); isDraggingRef.current = true }}
            aria-hidden={!interactive}
          />
        )}

        {/* Points over the track, precisely positioned */}
        <div className="absolute left-0 right-0 z-10" style={{ top: 0 }}>
          {stages.map((label, index) => {
            const isActive = masked ? (index < currentIndex) : (index <= uiIndex)
            const isCurrent = masked ? false : index === uiIndex
            const leftPercent = (index / (totalPoints - 1)) * 100
            return (
              <button
                key={label}
                type="button"
                 onClick={() => {
                   if (interactive) {
                     commitIndex(index)
                   } else if (index < currentIndex) {
                     setModalIndex(index)
                   }
                 }}
                 onMouseEnter={() => {
                   if (index < currentIndex) {
                     setHoverIndex(index)
                   }
                 }}
                 onMouseLeave={() => {
                   setHoverIndex(null)
                 }}
                className={[
                  'absolute -translate-x-1/2 -translate-y-1/2 w-5 h-5 md:w-6 md:h-6 rounded-full border-2 transition-colors duration-300',
                  isActive ? 'bg-secondary border-secondary' : 'bg-white border-white/30',
                  interactive && isCurrent ? 'ring-4 ring-secondary/30' : '',
                ].join(' ')}
                style={{ left: `${leftPercent}%`, top: barCenter }}
                aria-label={`Go to ${label}`}
                title={(details && details[index]) ? details[index] : (isActive ? 'Completed' : 'Pending')}
              />
            )
          })}
        </div>
        {/* Labels above the track, aligned with points */}
        <div className="absolute left-0 right-0 z-10" style={{ top: barCenter - 24 }}>
          {stages.map((label, index) => {
            const isActive = index <= currentIndex
            const leftPercent = (index / (totalPoints - 1)) * 100
            const transform = index === 0
              ? 'translate(0, -50%)'
              : index === totalPoints - 1
                ? 'translate(-100%, -50%)'
                : 'translate(-50%, -50%)'
            const align = index === 0 ? 'text-left' : index === totalPoints - 1 ? 'text-right' : 'text-center'
            // Rotate middle labels on mobile to prevent overlap
            const shouldRotate = index !== 0 && index !== totalPoints - 1
            return (
              <div
                key={label}
                className={`absolute ${align}`}
                style={{ left: `${leftPercent}%`, transform }}
              >
                <span 
                  className={`inline-block whitespace-nowrap text-[7px] sm:text-[8px] md:text-xs font-medium tracking-tight ${isActive ? 'text-secondary' : 'text-white/50'} ${shouldRotate ? 'md:rotate-0 -rotate-45 origin-left' : ''}`}
                  style={shouldRotate ? { transformOrigin: 'left center' } : {}}
                >
                  {label}
                </span>
              </div>
            )
          })}
        </div>
      </div>

      {/* Modal for full info on a completed stage */}
      {modalIndex !== null && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          onClick={() => setModalIndex(null)}
        >
          <div
            className="bg-black/90 backdrop-blur-md border border-secondary/20 rounded-xl shadow-2xl max-w-2xl w-full overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {images && images[modalIndex] ? (
              <div className="w-full h-64 md:h-80 bg-black/20 overflow-hidden">
                <img
                  src={images[modalIndex]}
                  alt={stages[modalIndex]}
                  className="w-full h-full object-cover"
                  onError={(e) => { e.currentTarget.style.display = 'none' }}
                />
              </div>
            ) : null}
            <div className="p-4 md:p-6">
              <div className="flex items-start justify-between mb-2">
                <h3 className="text-lg md:text-xl font-semibold text-white">{stages[modalIndex]}</h3>
                <button
                  className="text-white/50 hover:text-white"
                  onClick={() => setModalIndex(null)}
                  aria-label="Close"
                >
                  ✖
                </button>
              </div>
              <div className="text-sm md:text-base text-white/70 whitespace-pre-line">
                {details && details[modalIndex] ? details[modalIndex] : 'No details available.'}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ProgressBar


